// Centralized post-auth redirect decision. Used by App.js (initUser, the
// Google OAuth gcode callback) and by the TopNav project switcher, so the
// three call sites can never drift out of sync with each other.
//
// Order of precedence:
//   1. No projects at all -> pending invitation (non-expired) wins, else
//      send the user to create their first project.
//   2. Has projects, but the active one has zero connected platforms AND has
//      never had a data import (has_data_imports: false) -> send the user to
//      grant data-source access.
//   2b. Has projects, zero connected platforms, but HAS had a data import
//      (has_data_imports: true, e.g. previously connected then
//      disconnected) -> do NOT redirect to grant-access; fall through to the
//      normal landing route, where the existing GlobalMessages mechanism
//      (DataStateService.evaluateGlobalAccessMessage, already live) is
//      expected to surface the "no active connection, historical data
//      available" message. Multi-Platform Business Support TRD, Bounded
//      Unit B, Requirement B5 (Q17 fix) -- see cq-api Requirement A6 for the
//      has_data_imports field this depends on.
//   3. Otherwise -> normal post-auth landing page.

export const INVITATION_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000;

// Changed from '/analyze/dashboards/all' to '/analyze/insights' per
// daily-insights-engine-trd.md (Section 4, Bounded Unit 3) -- the insights
// homepage is now the post-auth/post-project-switch landing page. Wide
// blast radius: every post-auth navigation (initUser, Google OAuth gcode
// callback, TopNav project switcher) resolves through this constant, so
// changing it here changes all three call sites at once. See
// postAuthRoute.test.js's dedicated assertion for this value.
export const DEFAULT_LANDING_ROUTE = '/analyze/insights';

export function getPostAuthRoute(projects, pendingInvitations, currentProject) {
  const list = projects || [];

  if (list.length === 0) {
    const now = Date.now();
    const nonExpiredInvitations = (pendingInvitations || []).filter(
      inv => (now - Number(inv.created_at)) < INVITATION_EXPIRY_MS
    );
    if (nonExpiredInvitations.length > 0) {
      return '/accept-invite';
    }
    return '/create-project';
  }

  // Every other branch has a known project by this point (the
  // projects.length === 0 branch above is the only case with none) --
  // per connect-cta-new-tab-project-leak-trd.md Section 3 R3, both
  // remaining routes are now project-ID-prefixed, since /grant-access and
  // DEFAULT_LANDING_ROUTE are both project-scoped routes as of that TRD's
  // Section 2.2 route classification.
  const platforms = currentProject && currentProject.platforms;
  if (!platforms || platforms.length === 0) {
    // Requirement B5 (Q17 fix): a project with zero connected platforms
    // still only redirects to grant-access when it has NEVER had a data
    // import. `has_data_imports` reflects import history, not current
    // active status (cq-api AC-A6.3) -- a project that connected, imported
    // data, then disconnected everything must NOT be bounced back into
    // onboarding; it falls through to the normal landing route instead,
    // where GlobalMessages surfaces the "historical data available" message.
    if (!currentProject.has_data_imports) {
      return `/${currentProject.id}/grant-access`;
    }
    return `/${currentProject.id}${DEFAULT_LANDING_ROUTE}`;
  }

  return `/${currentProject.id}${DEFAULT_LANDING_ROUTE}`;
}

// Validates a `redirect_url` query-param value captured from the logged-out
// redirect flow (see App.js's buildLoggedOutRedirect/consumeRedirectUrl).
// Only same-origin, relative-path values are ever safe to navigate to after
// login -- anything else risks an open-redirect bypass. Rejects:
//   - values that don't start with '/' at all (covers absolute URLs with a
//     scheme, e.g. 'https://evil.com', 'javascript:alert(1)')
//   - protocol-relative URLs ('//evil.com') -- browsers resolve a leading
//     '//' against the current scheme to an absolute external URL despite
//     superficially "starting with a slash"
//   - backslash-prefixed values ('/\evil.com') -- some browsers normalize a
//     leading '/\' the same way as '//', another open-redirect bypass
export function isValidRedirectUrl(value) {
  return typeof value === 'string'
    && value.length > 0
    && value.startsWith('/')
    && !value.startsWith('//')
    && !value.startsWith('/\\');
}
